import { useState, useEffect } from 'react';
import { Wrench, Calendar, User, FileText, AlertCircle, ChevronDown, ChevronUp, Settings, Code, Activity, Database } from 'lucide-react';
import { ISavV2, SavV2Status } from '@/models/savV2';
import axiosInstance from '@/Api/axiosV2Config';

// ─── Interventions ───────────────────────────────────────────────────────────

interface Intervention {
  id: string;
  interventionNumber: number;
  reason: 'initial' | 'recurrence' | 'new_problem' | 'complementary';
  repairerUuid?: string;
  selectedQuoteId?: string;
  outcome: 'in_progress' | 'resolved' | 'partial' | 'failed';
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, unknown>;
}

const REASON_LABELS: Record<string, string> = {
  initial: 'Intervention initiale',
  recurrence: 'Récurrence',
  new_problem: 'Nouveau problème',
  complementary: 'Intervention complémentaire',
};

const OUTCOME_CONFIG: Record<string, { label: string; color: string }> = {
  in_progress: { label: 'En cours', color: 'tw-bg-yellow-100 tw-text-yellow-800 tw-border-yellow-300' },
  resolved: { label: 'Résolu', color: 'tw-bg-green-100 tw-text-green-800 tw-border-green-300' },
  partial: { label: 'Partiellement résolu', color: 'tw-bg-blue-100 tw-text-blue-800 tw-border-blue-300' },
  failed: { label: 'Échec', color: 'tw-bg-red-100 tw-text-red-800 tw-border-red-300' },
};

// ─── Collapsible ─────────────────────────────────────────────────────────────

function Collapsible({ title, icon, defaultExpanded = false, children }: {
  title: string;
  icon?: React.ReactNode;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultExpanded);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="tw-w-full tw-flex tw-items-center tw-justify-between tw-py-2 hover:tw-bg-gray-50 tw-transition-colors focus:tw-outline-none"
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          {icon && <span className="tw-text-gray-500">{icon}</span>}
          <span className="tw-text-sm tw-font-semibold tw-text-gray-900">{title}</span>
        </div>
        <span className="tw-text-gray-400">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>
      {open && <div className="tw-pt-2">{children}</div>}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

interface SavTechnicalBlockProps {
  sav: ISavV2;
}

export function SavTechnicalBlock({ sav }: SavTechnicalBlockProps) {
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/sav-v2/sav/${sav.id}/interventions`);
        if (!cancelled) {
          setInterventions(response.data);
          setError(null);
        }
      } catch {
        if (!cancelled) setError('Erreur lors du chargement des interventions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sav.id]);

  return (
    <div className="tw-bg-white tw-rounded-md tw-border tw-border-gray-200 tw-shadow-sm tw-p-4">
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
        <Settings className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
        <h3 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-m-0">Infos techniques</h3>
      </div>

      <div className="tw-space-y-3">
        {/* Interventions */}
        <Collapsible title="Interventions" icon={<Wrench size={14} />} defaultExpanded>
          {loading && (
            <div className="tw-flex tw-justify-center tw-py-4">
              <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-b-2 tw-border-blue-600" />
            </div>
          )}
          {error && (
            <div className="tw-flex tw-items-center tw-gap-2 tw-text-red-600 tw-text-sm">
              <AlertCircle size={14} />
              <span>{error}</span>
            </div>
          )}
          {!loading && !error && interventions.length === 0 && (
            <p className="tw-text-sm tw-text-gray-500 tw-italic">Aucune intervention</p>
          )}
          {!loading && !error && interventions.length > 0 && (
            <div className="tw-space-y-2">
              {interventions.map((intervention) => {
                const outcomeConfig = OUTCOME_CONFIG[intervention.outcome] || { label: intervention.outcome, color: 'tw-bg-gray-100 tw-text-gray-800' };
                return (
                  <div key={intervention.id} className="tw-border tw-border-gray-200 tw-rounded-md tw-p-3">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
                      <span className="tw-text-sm tw-font-medium tw-text-gray-900">
                        #{intervention.interventionNumber} — {REASON_LABELS[intervention.reason] || intervention.reason}
                      </span>
                      <span className={`tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-border ${outcomeConfig.color}`}>
                        {outcomeConfig.label}
                      </span>
                    </div>
                    <div className="tw-text-xs tw-text-gray-500 tw-space-y-0.5">
                      <div className="tw-flex tw-items-center tw-gap-1">
                        <Calendar size={12} />
                        <span>Créée le {new Date(intervention.createdAt).toLocaleDateString('fr-FR')}</span>
                      </div>
                      {intervention.repairerUuid && (
                        <div className="tw-flex tw-items-center tw-gap-1">
                          <User size={12} />
                          <span>Réparateur : {intervention.repairerUuid}</span>
                        </div>
                      )}
                      {intervention.selectedQuoteId && (
                        <div className="tw-flex tw-items-center tw-gap-1">
                          <FileText size={12} />
                          <span>Devis : {intervention.selectedQuoteId}</span>
                        </div>
                      )}
                    </div>
                    {intervention.metadata && Object.keys(intervention.metadata).length > 0 && (
                      <pre className="tw-bg-gray-50 tw-p-2 tw-rounded tw-text-[10px] tw-overflow-x-auto tw-mt-2">
                        {JSON.stringify(intervention.metadata, null, 2)}
                      </pre>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Collapsible>

        <hr className="tw-border-gray-200" />

        {/* Actions */}
        <Collapsible title="Actions" icon={<Activity size={14} />}>
          <div className="tw-space-y-2">
            {sav.status === SavV2Status.DRAFT && (
              <p className="tw-text-xs tw-text-gray-500">Brouillon — suppression possible</p>
            )}
            {sav.status === SavV2Status.ACTIVE && (
              <p className="tw-text-xs tw-text-gray-500">Actif — assignation réparateur / demande devis</p>
            )}
            {sav.status !== SavV2Status.DRAFT && sav.status !== SavV2Status.ACTIVE && (
              <p className="tw-text-xs tw-text-gray-500 tw-italic">Aucune action disponible pour ce statut</p>
            )}
          </div>
        </Collapsible>

        <hr className="tw-border-gray-200" />

        {/* Métadonnées */}
        <Collapsible title="Métadonnées" icon={<Database size={14} />}>
          <div className="tw-bg-gray-50 tw-p-3 tw-rounded-md">
            <dl className="tw-grid tw-grid-cols-2 tw-gap-2 tw-text-xs">
              <div>
                <dt className="tw-font-medium tw-text-gray-500">ID unique</dt>
                <dd className="tw-text-gray-900 tw-font-mono">{sav.uniqueId}</dd>
              </div>
              <div>
                <dt className="tw-font-medium tw-text-gray-500">Statut</dt>
                <dd className="tw-text-gray-900">{sav.status}</dd>
              </div>
              <div>
                <dt className="tw-font-medium tw-text-gray-500">Intervention courante</dt>
                <dd className="tw-text-gray-900 tw-font-mono">{sav.currentInterventionId || 'Aucune'}</dd>
              </div>
              <div>
                <dt className="tw-font-medium tw-text-gray-500">Version</dt>
                <dd className="tw-text-gray-900">SAV V2</dd>
              </div>
            </dl>
          </div>
        </Collapsible>

        <hr className="tw-border-gray-200" />

        {/* Infos système */}
        <Collapsible title="Infos système" icon={<Code size={14} />}>
          <div className="tw-bg-gray-50 tw-p-3 tw-rounded-md">
            <dl className="tw-space-y-1 tw-text-xs">
              <div>
                <dt className="tw-font-medium tw-text-gray-500 tw-inline">Créé le : </dt>
                <dd className="tw-text-gray-900 tw-inline">{sav.createdAt ? new Date(sav.createdAt).toLocaleString('fr-FR') : 'N/A'}</dd>
              </div>
              <div>
                <dt className="tw-font-medium tw-text-gray-500 tw-inline">Modifié le : </dt>
                <dd className="tw-text-gray-900 tw-inline">{sav.updatedAt ? new Date(sav.updatedAt).toLocaleString('fr-FR') : 'N/A'}</dd>
              </div>
              <div>
                <dt className="tw-font-medium tw-text-gray-500 tw-inline">Déclaré le : </dt>
                <dd className="tw-text-gray-900 tw-inline">{sav.declarationDate ? new Date(sav.declarationDate).toLocaleString('fr-FR') : 'N/A'}</dd>
              </div>
            </dl>
          </div>
        </Collapsible>

        <hr className="tw-border-gray-200" />

        {/* Debug JSON */}
        <Collapsible title="Données brutes (Debug)" icon={<Code size={14} />}>
          <div className="tw-bg-gray-900 tw-text-gray-100 tw-p-3 tw-rounded-lg tw-overflow-x-auto">
            <pre className="tw-text-[10px]">
              {JSON.stringify(sav, null, 2)}
            </pre>
          </div>
        </Collapsible>
      </div>
    </div>
  );
}
